export interface TurnoverInvoicesBuyerRaw {
  buyername: string | null;
  turnoverinvoice_detail?: {
    matched_buyer_name?: string | null;
  } | null;
}

export interface TurnoverInvoicesBuyersResponse {
  turnoverinvoice: Array<{
    sellername: string | null;
    buyername: string | null;
    turnoverinvoice_detail?: {
      matched_buyer_name?: string | null;
      matched_distributor_name?: string | null;
      distributordetails?: { name?: string | null } | null;
    } | null;
  }>;
}

export interface TurnoverInvoicesBuyersRequest {
  sellerWorkspaceId: string;
  where: Record<string, unknown>;
  limit: number;
  offset: number;
}

export type TurnOverInvoiceStatus = 'Pending' | 'Approved' | 'Rejected';

export type TurnOverInvoiceValidationStatus =
  | 'Parsing the invoice'
  | 'Parsing Failed'
  | 'Distributor Not Found'
  | 'Buyer Not Found'
  | 'Invoice Data Incomplete'
  | 'Duplicate Invoice'
  | 'Products Unmapped'
  | 'Partially Mapped'
  | 'Successful'
  | 'Likely Fraudulent';

// Map database enum values to display names for invoice status
export const INVOICE_STATUS_MAP: Record<string, TurnOverInvoiceStatus> = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const INVOICE_STATUS_REVERSE_MAP: Record<TurnOverInvoiceStatus, string> =
  {
    Pending: 'PENDING',
    Approved: 'APPROVED',
    Rejected: 'REJECTED',
  };

// Map database enum values to display names for validation status
export const VALIDATION_STATUS_MAP: Record<
  string,
  TurnOverInvoiceValidationStatus
> = {
  PARSING_FAILED: 'Parsing Failed',
  DISTRIBUTOR_NOT_FOUND: 'Distributor Not Found',
  BUYER_NOT_FOUND: 'Buyer Not Found',
  INCOMPLETE_DATA: 'Invoice Data Incomplete',
  UNMAPPED_PRODUCT: 'Products Unmapped',
  PARTIALLY_MAPPED: 'Partially Mapped',
  DUPLICATE_INVOICE: 'Duplicate Invoice',
  LIKELY_FRAUDULENT: 'Likely Fraudulent',
  SUCCESSFUL: 'Successful',
};

export const VALIDATION_STATUS_REVERSE_MAP: Record<
  TurnOverInvoiceValidationStatus,
  string
> = {
  'Parsing the invoice': '',
  'Parsing Failed': 'PARSING_FAILED',
  'Distributor Not Found': 'DISTRIBUTOR_NOT_FOUND',
  'Buyer Not Found': 'BUYER_NOT_FOUND',
  'Invoice Data Incomplete': 'INCOMPLETE_DATA',
  'Products Unmapped': 'UNMAPPED_PRODUCT',
  'Partially Mapped': 'PARTIALLY_MAPPED',
  'Duplicate Invoice': 'DUPLICATE_INVOICE',
  'Likely Fraudulent': 'LIKELY_FRAUDULENT',
  Successful: 'SUCCESSFUL',
};

export type TurnOverInvoiceListItem = {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  submittedByName: string;
  submittedOn: string;
  status: TurnOverInvoiceStatus;
  /** First/primary validation status (for backward compat and single display) */
  validationStatus: TurnOverInvoiceValidationStatus | null;
  /** All validation statuses from invoice_validation_status table */
  validationStatuses: TurnOverInvoiceValidationStatus[];
  distributorName: string;
  distributorContactName: string;
  buyerName: string;
  invoiceSkus: number;
  invoiceAmount: number;
  taxableValue: number | null;
  gst_percentage: number | null;
  gst_amount: number | null;
  discount: number | null;
  secondary_discount: number | null;
  cumulative_discount: number | null;
  discount_amount: number | null;
  cess: number | null;
  approved_rejected_by: string | null;
  remark: string | null;
  contributionSkus: number | null;
  contributionAmount: number | null;
};

export interface TurnoverInvoiceRaw {
  id: string;
  invoice_number: string;
  invoice_amount: number;
  invoice_date: string;
  submittedby: string;
  status: string;
  validationStatus?: string | null;
  invoice_validation_status?: Array<{ validation_status: string }> | null;
  turnoverinvoice_detail?: {
    buyerdetails?: { name?: string; address?: string; gstin?: string } | null;
    distributordetails?: {
      name?: string;
      address?: string;
      gstin?: string;
    } | null;
    matched_buyer_name?: string | null;
    matched_distributor_name?: string | null;
  } | null;
  buyername: string;
  sellername: string;
  created_at: string;
  taxableValue?: number | null;
  gst_percentage?: number | null;
  gst_amount?: number | null;
  discount?: number | null;
  secondary_discount?: number | null;
  cumulative_discount?: number | null;
  discount_amount?: number | null;
  cess?: number | null;
  approved_rejected_by?: string | null;
  remark?: string | null;
  turnoverinvoice_lines_aggregate?: {
    aggregate?: { count: number } | null;
  } | null;
}

export interface TurnoverContributionRaw {
  id: string;
  turnoverinvoice_lines_aggregate?: {
    aggregate?: {
      count: number;
      sum?: {
        contribution?: number | null;
        total_amount?: number | null;
        total_amount_with_tax?: number | null;
      } | null;
    } | null;
  } | null;
}

export interface TurnoverInvoicePartyRaw {
  name?: string | null;
  address?: string | null;
  gstin?: string | null;
  phone_number?: string | null;
  email?: string | null;
}

export interface TurnoverInvoiceMatchedDetailsRaw {
  matched_by?: string | null;
  matched_data?: {
    supplierName?: string | null;
    name?: string | null;
    phone?: string | null;
    email?: string | null;
    GSTNumber?: string | null;
    physicalAddress?: {
      address1?: string | null;
    } | null;
  } | null;
}

export interface TurnoverInvoiceLineRaw {
  id: string;
  serial_no?: number | null;
  product_name?: string | null;
  mapped_product_name?: string | null;
  sku_code?: string | null;
  hsn_code?: string | null;
  unit_price?: number | null;
  quantity?: number | null;
  discount?: number | null;
  total_amount?: number | null;
  contribution?: number | null;
  status?: string | null;
  scheme_id?: string | null;
  total_amount_with_tax?: number | null;
  turnover_scheme?: {
    name?: string | null;
    code?: string | null;
  } | null;
}

export interface TurnoverInvoiceDetailsRaw {
  id: string;
  invoice_number?: string | null;
  invoice_amount?: number | null;
  invoice_date?: string | null;
  submittedby?: string | null;
  status?: string | null;
  validationStatus?: string | null;
  invoice_validation_status?: Array<{
    validation_status?: string | null;
  }> | null;
  buyername?: string | null;
  sellername?: string | null;
  created_at?: string | null;
  invoice_file?: string[] | string | null;
  cfa_id?: string | null;
  customerId?: string | null;
  turnoverinvoice_detail?: {
    buyerdetails?: TurnoverInvoicePartyRaw | null;
    distributordetails?: TurnoverInvoicePartyRaw | null;
    matched_buyer_details?: TurnoverInvoiceMatchedDetailsRaw | null;
    matched_distributor_details?: TurnoverInvoiceMatchedDetailsRaw | null;
  } | null;
  mapped_products_aggregate?: {
    aggregate?: {
      sum?: {
        total_amount?: number | null;
        total_amount_with_tax?: number | null;
      } | null;
    } | null;
  } | null;
  contribution_lines_aggregate?: {
    aggregate?: {
      sum?: {
        contribution?: number | null;
        total_amount?: number | null;
        total_amount_with_tax?: number | null;
      } | null;
    } | null;
  } | null;
  turnoverinvoice_lines?: TurnoverInvoiceLineRaw[] | null;
  turnoverinvoice_lines_aggregate?: {
    aggregate?: { count?: number | null } | null;
  } | null;
}

export interface TurnoverInvoiceDetailsExtraRaw {
  id: string;
  invoice_id?: string | null;
  buyerdetails?: TurnoverInvoicePartyRaw | null;
  distributordetails?: TurnoverInvoicePartyRaw | null;
  company_name?: string | null;
}

export interface TurnoverInvoicesListResponse {
  turnoverinvoice: TurnoverInvoiceRaw[];
  turnoverinvoice_aggregate: {
    aggregate: { count: number };
  };
}

export interface TurnoverInvoicesContributionResponse {
  turnoverinvoice: TurnoverContributionRaw[];
}

export interface TurnoverInvoiceDetailsResponse {
  turnoverinvoice: TurnoverInvoiceDetailsRaw[];
  turnoverinvoice_details: TurnoverInvoiceDetailsExtraRaw[];
}

export interface TurnoverInvoicesListRequest {
  sellerWorkspaceId: string;
  where?: Record<string, unknown>;
  limit: number;
  offset: number;
}

export interface TurnoverInvoicesContributionRequest {
  sellerWorkspaceId: string;
  where?: Record<string, unknown>;
  limit: number;
  offset: number;
}

export interface TurnoverInvoiceDetailsRequest {
  sellerWorkspaceId: string;
  invoiceId: string;
}

export interface TurnoverPartyDetails {
  name: string;
  phone: string;
  email: string;
  address: string;
  gst: string;
}

export interface TurnoverInvoiceOverviewData {
  totalInvoiceValue: number;
  ourProductsValue: number;
  contributionValue: number;
  distributor: TurnoverPartyDetails;
  buyer: TurnoverPartyDetails;
  submittedByName: string;
  submittedOn: string;
  invoiceUrl: string | null;
}

export type MappingStatus = 'mapped' | 'brand_not_found' | 'unmapped';

export interface TurnoverInvoiceItemView {
  id: string;
  name: string;
  price: number;
  quantity: number;
  discount: number;
  totalAmount: number;
  contributionAmount: number;
  appliedScheme: string;
  sku: string;
  hsn: string;
  status: MappingStatus;
}
